import React, { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";

/** Create or edit a group: its name, a note, and its collection sites. */
export const SiteGroupDialog = ({ open, group, sites, onClose, onSave, onDelete }) => {
  const [form, setForm] = useState({ name: "", description: "", trapSiteIds: [] });
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setForm({
        name: group?.name ?? "",
        description: group?.description ?? "",
        trapSiteIds: (group?.trapSiteIds ?? []).map(String),
      });
      setError(null);
    }
  }, [open, group]);

  const nameOf = (id) => sites.find((s) => s.id === id)?.name ?? `Site ${id}`;
  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave({ id: group?.id, ...form, name: form.name.trim() });
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || "The group could not be saved.");
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    setSaving(true);
    try {
      await onDelete(group.id);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || "The group could not be deleted.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="site-group-title">
      <DialogTitle id="site-group-title">{group ? "Edit site group" : "New site group"}</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
        <TextField
          label="Name"
          size="small"
          required
          autoFocus
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          helperText="Unique in your organization, ignoring case"
          inputProps={{ maxLength: 80 }}
        />
        <TextField
          label="Description"
          size="small"
          multiline
          minRows={2}
          value={form.description}
          onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          inputProps={{ maxLength: 500 }}
        />
        <Autocomplete
          multiple
          size="small"
          disableCloseOnSelect
          options={sites.map((s) => s.id)}
          getOptionLabel={nameOf}
          value={form.trapSiteIds}
          onChange={(_, v) => setForm((f) => ({ ...f, trapSiteIds: v }))}
          renderOption={({ key, ...props }, id, { selected }) => (
            <li key={key} {...props}>
              <Checkbox size="small" checked={selected} sx={{ mr: 1, p: 0.5 }} />
              {nameOf(id)}
            </li>
          )}
          renderInput={(params) => (
            <TextField {...params} label="Collection sites" helperText="A site can be in more than one group" />
          )}
        />
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {group && (
          <Button color="error" onClick={remove} disabled={saving} sx={{ mr: "auto" }}>
            Delete group
          </Button>
        )}
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" disableElevation onClick={submit} disabled={saving || !form.name.trim()}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
